import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { StyledCreditCardForm } from './CreditCardForm.styles';

import {
  CreditCardFormProps,
  StripeCreditCardFormProps,
} from './CreditCardForm.types';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

const StripeCreditCardForm = forwardRef(
  (
    {
      billingData,
      style,
      iconStyle,
      classes,
      hideIcon,
      disabled,
      labels,
      placeholders,
      handleStripeToken,
      handleError,
      handleChange,
      handleSubmit,
      componentClassname,
    }: StripeCreditCardFormProps,
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();

    const options = {
      style,
      iconStyle,
      value: { postalCode: billingData.address_zip }, // https://stripe.com/docs/js/elements_object/create_element?type=card#elements_create-options-value
      classes,
      hidePostalCode: true,
      showIcon: !hideIcon,
      disabled,
    };

    const coreHandleSubmit = async () => {
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      if (handleSubmit) {
        handleSubmit();
      }

      const card = elements.getElement(CardNumberElement);
      const result = await stripe.createToken(card, billingData);

      if (result.error) {
        // Show error to your customer.
        handleError(result.error.message);

        // Return failed result
        return { success: false };
      } else {
        // Function to help send the token to the server.
        handleStripeToken(result.token);

        // Return success result
        return { success: true };
      }
    };

    useImperativeHandle(ref, () => ({
      async handleSubmit() {
        const result = await coreHandleSubmit();

        return result;
      },
      isStripeLoaded() {
        return !!stripe;
      },
    }));

    const getLabel = (fieldName: 'cvc' | 'number' | 'expiry') => {
      if (!labels || !labels[fieldName]) {
        return null;
      }

      return (
        <div className={`${componentClassname}__label`}>
          {labels[fieldName]}
        </div>
      );
    };

    return (
      <div className={`${componentClassname}__fieldset`}>
        <div className={`${componentClassname}__card-number`}>
          {getLabel('number')}
          <CardNumberElement
            options={{
              ...options,
              placeholder: placeholders.number,
            }}
            onChange={handleChange}
          />
        </div>
        <div className={`${componentClassname}__card-expiry`}>
          {getLabel('expiry')}
          <CardExpiryElement
            options={{
              ...options,
              placeholder: placeholders.expiry,
            }}
            onChange={handleChange}
          />
        </div>
        <div className={`${componentClassname}__card-cvc`}>
          {getLabel('cvc')}
          <CardCvcElement
            options={{
              ...options,
              placeholder: placeholders.cvc,
            }}
            onChange={handleChange}
          />
        </div>
      </div>
    );
  },
);

//TODO Remove Duplicate "wrapperCustomClass" prop (duplicate of "customClassnames")
const CreditCardForm = forwardRef(
  (
    {
      stripePromise,
      billingData,
      style,
      iconStyle = 'default',
      hideIcon = false,
      disabled = false,
      stripeClasses,
      labels,
      placeholders,
      handleStripeToken,
      handleError,
      handleChange,
      handleSubmit,
      componentClassname = 'credit-card-form',
      wrapperCustomClass = '',
      customClassnames = '',
      customStyle = '',
    }: CreditCardFormProps,
    ref,
  ) => {
    const creditCardFormRef = useRef(null);

    useImperativeHandle(ref, () => ({
      async handleSubmit() {
        const result = await creditCardFormRef.current.handleSubmit();

        return result;
      },
      isStripeLoaded() {
        creditCardFormRef.current.isStripeLoaded();
      },
    }));

    return (
      <StyledCreditCardForm
        className={`${componentClassname} ${wrapperCustomClass} ${customClassnames}`}
        customStyle={customStyle}
      >
        <Elements stripe={stripePromise}>
          <StripeCreditCardForm
            ref={creditCardFormRef}
            billingData={billingData}
            style={style}
            hideIcon={hideIcon}
            iconStyle={iconStyle}
            classes={stripeClasses}
            componentClassname={componentClassname}
            disabled={disabled}
            labels={labels}
            placeholders={placeholders}
            handleStripeToken={handleStripeToken}
            handleError={handleError}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
          />
        </Elements>
      </StyledCreditCardForm>
    );
  },
);
CreditCardForm.displayName = 'CreditCardForm';

export default CreditCardForm;
